import { Button, Pill, Popover, PopoverContent, RadioButton } from '@idexx-laboratories/spot-react';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import React, { useState } from 'react';
import Grid, { GridRendererProps } from '../../components/Grid/Grid';
import Loader from '../../components/Loader/Loader';
import { useStore } from '../../hooks/use-store';
import { type PracticeHospitalResponse, PracticeResponse } from '../../types/practices-types';
import { isDefined } from '../../utils/object-utils';
import DisconnectPracticeModalContent from './DisconnectPracticeModalContent';
import EnterHospitalIdModalContent from './EnterHospitalIdModalContent';

export const TrupanionHospitalRenderer = observer(
  (props: GridRendererProps<PracticeResponse> & { index: number }) => {
    const { row, index } = props;
    const { connectPracticeStore: store, globalStore, globalModalStore } = useStore();
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const { loaders } = store;
    const primaryPractice = globalStore.practices.find(p => p.isPrimary);

    const confirmDisconnectPractice = () => {
      globalModalStore.showOkCancelPopup(
        'Disconnect Practice',
        React.createElement(DisconnectPracticeModalContent, { practiceName: row.name1 }),
        'Continue',
        'Cancel',
        () => void store.updatePracticesProperties(index),
        undefined,
        false,
        true,
      );
    };

    const addManually = () => {
      globalModalStore.showOkCancelPopup(
        "Enter Your Hospital's Unique ID",
        React.createElement(EnterHospitalIdModalContent),
        'Submit',
        'Cancel',
        () => void store.getTrupanionHospitalById(index),
        undefined,
        false,
        true,
      );
    };

    if (isDefined(row.hospitalId)) {
      return (
        <>
          <Popover
            side='bottom'
            isOpen={isOpen}
            onOpenChange={setIsOpen}
            data-testid={`practice-hospital-id-${row.id}-popover`}
            content={
              <PopoverContent size='small' style={{ fontSize: '13px', width: '290px' }}>
                {row.hospitalId}
              </PopoverContent>
            }
            id={`practice-hospital-id-${row.id}`}
          >
            <Pill
              color='positive'
              size='small'
              data-testid={`connected-practice-${row.id}-btn`}
              label='Connected'
            />
          </Popover>
          <Button
            iconLeft='cancel'
            aria-label='disconnect'
            size='small'
            variant='link'
            onClick={() => confirmDisconnectPractice()}
            disabled={loaders.connect || loaders.disconnect}
            data-testid={`disconnect-practice-${row.id}-btn`}
          >
            <Loader loading={loaders.disconnect}>Disconnect</Loader>
          </Button>
        </>
      );
    }
    return (
      <>
        {isDefined(primaryPractice) &&
          row.id !== primaryPractice.id &&
          isDefined(primaryPractice.hospitalId) && (
            <Button
              iconLeft='home'
              size='small'
              aria-label='home'
              variant='link'
              onClick={() =>
                void store.updatePracticesProperties(index, primaryPractice?.hospitalId)
              }
              disabled={loaders.connect || loaders.disconnect}
              data-testid={`use-primary-${row.id}-btn`}
            >
              <Loader loading={loaders.connect}>Use Primary</Loader>
            </Button>
          )}
        <Button
          iconLeft='show'
          size='small'
          variant='link'
          aria-label='show'
          disabled={isDefined(loaders.searchHospitalIndex) || isDefined(store.lookupPracticeIndex)}
          onClick={() => void store.searchForTrupanionHospital(row, index)}
          data-testid={`lookup-${row.id}-btn`}
        >
          <Loader loading={loaders.searchHospitalIndex === index}>Look Up</Loader>
        </Button>
        <Button
          iconLeft='add'
          size='small'
          aria-label='add'
          variant='link'
          disabled={
            !store.addManuallyEnabled[row.id] ||
            isDefined(loaders.getHospitalByIdIndex) ||
            isDefined(store.lookupPracticeIndex)
          }
          data-testid={`add-manually-${row.id}-btn`}
          onClick={() => addManually()}
        >
          <Loader loading={loaders.searchHospitalIndex === index}>Add Manually</Loader>
        </Button>
      </>
    );
  },
);

export const SubrowPracticeNameRenderer = observer(
  (props: GridRendererProps<PracticeHospitalResponse>) => {
    const { row, index } = props;
    const store = useStore().connectPracticeStore;
    return (
      <RadioButton
        id={`practice-name__${index}`}
        className='practice-name-rb'
        checked={store.subrowPracticeIndex === index}
        onChange={checked => checked && runInAction(() => (store.subrowPracticeIndex = index))}
        data-testid={`practice-name-rb-${index}`}
        label={row.name}
      />
    );
  },
);

const SubrowActionsRenderer = observer(
  (props: GridRendererProps<PracticeHospitalResponse> & { baseRowIndex: number }) => {
    const { row, index, baseRowIndex } = props;
    const store = useStore().connectPracticeStore;
    return (
      <>
        <Button
          variant='link'
          iconLeft='refresh-redo'
          aria-label='connect-trupanion'
          disabled={store.subrowPracticeIndex !== index}
          onClick={() => void store.connectHospitalId(baseRowIndex, row.hospitalId)}
          data-testid={`connect-trupanion-${index}-btn`}
        >
          <Loader loading={store.loaders.connect}>Connect</Loader>
        </Button>
      </>
    );
  },
);

export const SubRowRenderer = observer((props: GridRendererProps<PracticeResponse>) => {
  const { row, index } = props;
  const store = useStore().connectPracticeStore;

  return (
    <div key={`connect-practice-subrow-${row.id}`} className='subrow-wrapper'>
      <div className='subrow-info-wrapper'>
        <span className='displaying-label'>
          Look Up found {store.hospitalOptions.length} matches
        </span>
        <Button
          className='cancel-btn'
          aria-label='cancel-connecting'
          variant='link'
          size='small'
          onClick={() => store.cancelLookup(index)}
          data-testid='cancel-connecting-btn'
        >
          Cancel
        </Button>
      </div>
      <Grid
        id={`connect-practice-grid-${row.id}`}
        className='connect-practice-subrow-grid'
        columns={store.subRowColDefs(index)}
        data={store.hospitalOptions}
      />
    </div>
  );
});

export const nameRenderer = (props: GridRendererProps<PracticeResponse>): string => {
  const { row } = props;
  return [row.name1, row.name2, row.isPrimary ? '(Primary)' : ''].filter(p => p).join(' ');
};

export const addressRenderer = (props: GridRendererProps<PracticeResponse>): string => {
  const { row } = props;
  return [row.address1, row.address2, row.city, row.stateProvince, row.postalCode]
    .filter(p => p)
    .join(' ');
};

export const subrowActionsRenderer =
  (baseRowIndex: number) =>
  (props: GridRendererProps<PracticeHospitalResponse>): React.JSX.Element => (
    <SubrowActionsRenderer baseRowIndex={baseRowIndex} {...props} />
  );
